import { createContext, useContext, useEffect } from "react";

export const LanguageContext = createContext("it");
export const useLang = () => useContext(LanguageContext);

export function LanguageProvider({ lang, children }) {
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  return <LanguageContext.Provider value={lang}>{children}</LanguageContext.Provider>;
}

export function useLocalizedPath(path) {
  const lang = useContext(LanguageContext);
  return lang === "it" ? path : `/${lang}${path}`;
}

// Fallback: se una chiave manca, i componenti mostrano il testo italiano originale
export function useTr() {
  const lang = useContext(LanguageContext);
  return (key, fallback) => {
    const v = translations[lang]?.[key];
    return v === undefined ? fallback : v;
  };
}

export const HOME_PATHS = { it: "/", en: "/en", de: "/de" };

export const langMeta = {
  it: {
    title: "DJ Matrimoni Lugano e Ticino | Eventi in Svizzera – DJ Magix",
    description:
      "DJ a Lugano e in tutto il Ticino per matrimoni, feste ed eventi aziendali. Audio e luci fino a 1000 persone. Preventivo gratuito e senza impegno.",
    ogLocale: "it_IT",
  },
  en: {
    title: "Wedding DJ Lugano & Ticino | Swiss Events – DJ Magix",
    description:
      "Wedding & event DJ in Lugano and all of Ticino. Pro sound and lights for up to 1000 guests. Free, no-obligation quote.",
    ogLocale: "en_US",
  },
  de: {
    title: "Hochzeits-DJ Lugano & Tessin | Events Schweiz – DJ Magix",
    description:
      "Hochzeits-DJ in Lugano und im ganzen Tessin. Ton und Licht für bis zu 1000 Gäste. Kostenloses, unverbindliches Angebot.",
    ogLocale: "de_CH",
  },
};

export const translations = {
  en: {
    // Navbar
    "nav.services": "Services",
    "nav.genres": "Genres",
    "nav.experience": "About",
    "nav.gallery": "Gallery",
    "nav.contact": "Contact",
    // Hero
    "hero.badge": "25 Years of Experience",
    "hero.srTitle": "DJ sets for weddings, parties and events in Switzerland — ",
    "hero.tagline": "Music • Emotion • Technology",
    "hero.subtitle":
      "High-end DJ sets across Switzerland: birthdays, village festivals, weddings, clubs and corporate events. Producer & tailor-made sound.",
    "hero.ctaQuote": "Request a Free Quote",
    "hero.ctaCall": "Call Now",
    "hero.scroll": "Scroll",
    // StatsBar
    "stats.items": [
      "25+ Years of Career",
      "1500+ Events Delivered",
      "Professional Sound & Lighting Rig",
      "Village Festivals & Clubs in Switzerland",
      "Cold Sparks & Special Effects",
      "Producer & Custom Edits",
    ],
    // Intro (LandingPage)
    "intro.title": "The DJ for Your Event in Switzerland",
    "intro.p1":
      "Planning a wedding on Lake Lugano? A village festival in Ticino, a special birthday or a company party? With Magix you get one professional for music, sound and lights. I've been making Italian-speaking Switzerland dance for 25 years, with over 1500 events behind me.",
    "intro.p2":
      "The repertoire ranges from 80s/90s revival to disco dance, afro house and techno. We build the playlist together, weeks before the date. And if needed, I'll visit the venue in advance.",
    "intro.bullets": [
      "Professional sound and lights for up to 1000 guests",
      "Playlist built to measure",
      "Free venue inspection",
      "Wireless microphones for speeches and ceremony",
    ],
    "intro.p3":
      "I work throughout Ticino and travel to Zurich, Geneva, Bern and Basel. I also cover Northern Italy: Como, Milan and Varese. Prices start at CHF 150. The quote is free and non-binding.",
    "intro.links": [
      "Wedding DJ in Lugano",
      "Wedding DJ in Ticino",
      "Lake Como wedding DJ",
      "Corporate events in Switzerland",
      "Birthdays & private parties",
      "Village festivals & festivals",
    ],
    // Services
    "services.eyebrow": "My Services",
    "services.title": "High-End DJ Sets",
    "services.accent":
      "Every event is a tailor-made production: music, lights and emotions curated down to the smallest detail.",
    "services.items": [
      { title: "Village Festivals & Fairs", tag: "Popular Energy & Big Squares", desc: "Decades of experience making entire communities dance: crowd management, powerful amplification and sweeping playlists for all generations." },
      { title: "Birthdays & 18th Parties", tag: "Exclusive & Personalised Party", desc: "From the 18th to the 50th and beyond: a bespoke playlist with your favourite tracks, professional karaoke and architectural lighting." },
      { title: "Weddings of Excellence", tag: "Ticino & Lake Como", desc: "A flawless soundtrack from the ceremony to the aperitif and the wild party, with cold sparks and scenic lighting effects." },
      { title: "Clubs, Discos & Lounges", tag: "Beat Precision & Club Culture", desc: "Energetic DJ sets, fluid mixing and cutting-edge tracklists, with residencies and nights in Switzerland's most renowned clubs." },
      { title: "Corporate Events & Galas", tag: "Corporate & Brand Launch", desc: "A sophisticated atmosphere for company dinners, product launches and conventions, with wireless microphones and flawless audio direction." },
      { title: "DJ Producer & Custom Edits", tag: "Studio & Original Productions", desc: "Exclusive remixes, original mashups and productions that give your event a unique musical identity." },
    ],
    // Genres
    "genres.eyebrow": "The Sound",
    "genres.title": "The Genres of the DJ Set",
    "genres.accent":
      "Four musical souls, one direction of the dancefloor. Tap a genre and listen to the preview.",
    "genres.items": [
      { title: "80s/90s", sub: "Revival Dance", desc: "The great classics that made dance history: from Gigi D'Agostino to timeless anthems. Madonna, Spagna, Baltimora and Eiffel 65 on the decks: sing-alongs guaranteed from the first track." },
      { title: "Disco Dance", sub: "Nu-Disco & Funk", desc: "Timeless grooves, warm basslines and glitter: the floor lights up with the most elegant disco sound. From Earth, Wind & Fire to Daft Punk, the right groove for every generation." },
      { title: "Afro House", sub: "Deep & Tribal", desc: "Hypnotic percussion and deep atmospheres: the sound conquering clubs worldwide. A journey between African roots and modern electronics, perfect at sunset." },
      { title: "Techno", sub: "Melodic & Driving", desc: "Powerful beats and enveloping melodies for high-intensity nights until late. From the melodic techno of European festivals to harder sounds, always tailored to the floor." },
    ],
    "genres.listen": "Listen to the mix",
    "genres.playing": "Playing — tap to stop",
    // MixPlayer
    "mix.label": "Latest mix — Free Download",
    "mix.play": "Play",
    "mix.pause": "Pause",
    "mix.download": "Download the mix",
    // About
    "about.eyebrow": "About Me",
    "about.title": "25 Years of Passion Behind the Decks",
    "about.quote": "A quarter of a century of music, emotions and technology.",
    "about.p1":
      "For over 25 years DJ Magix has been setting the dancefloors of Switzerland and Northern Italy on fire: from Ticino village festivals to the most exclusive clubs, from dream weddings to major corporate events. His strength is reading the floor and creating the perfect alchemy between generations and genres.",
    "about.p2":
      "As a producer he creates exclusive remixes and custom edits: every event has a soundtrack you won't find anywhere else.",
    "about.p3":
      "An entertainer and dancer as well as a producer: when needed he hits the floor and wins over even the shyest guests. He works in villas, castles, restaurants and squares. Every event is hosted in Italian, with playlist and announcements prepared in advance — useful when guests arrive from all over the world.",
    "about.highlights": ["Years of Experience", "Events Delivered", "Passion & Dedication"],
    // Gallery
    "gallery.eyebrow": "Gallery",
    "gallery.title": "Golden Atmospheres & Moments",
    "gallery.accent": "Glimpses of the nights that made Switzerland dance.",
    "gallery.items": [
      { alt: "Wedding with confetti and 360 photobooth", caption: "Wedding — 360° Party" },
      { alt: "Party in a Ticino venue with DJ Magix disco lights", caption: "Live — Venue Lights" },
      { alt: "DJ console in a nightclub", caption: "Decks — Club Night" },
      { alt: "Crowd at a lakeside village festival in the evening with DJ Magix", caption: "Village Festival — Night" },
      { alt: "Golden sparks", caption: "Sparks" },
      { alt: "Newlyweds among the sparks", caption: "Wedding — First Dance" },
    ],
    // Testimonials (le citazioni restano in italiano: parole vere dei clienti)
    "testimonials.eyebrow": "Reviews",
    "testimonials.title": "What They Say About Me",
    "testimonials.accent": "The satisfaction of those who danced with DJ Magix.",
    "testimonials.badge.pre": "on Matrimonio.com · Recommended by",
    "testimonials.badge.post": "of couples",
    // Case Studies
    "cases.eyebrow": "Case Study",
    "cases.title": "Featured Events",
    "cases.accent": "Two collaborations I'm particularly proud of.",
    "cases.items": [
      { tag: "Public Authority — Village Festival", title: "Municipality of Paradiso (Lugano)", text: "For the public festival of the Municipality of Paradiso I curated the entire evening: sound and lighting for the square, a playlist designed for all generations and a grand finale with a packed floor. An honour to play for my home town." },
      { tag: "Events Agency — International", title: "Kika Events (Brazil)", text: "The Brazilian agency Kika Events chose me for the musical side of an international private event in Switzerland: a multilingual DJ set, remote coordination with the agency and a night that united guests from two continents." },
    ],
    // Blog teaser
    "blog.eyebrow": "From the Blog",
    "blog.title": "Tips for Your Event",
    "blog.accent":
      "Practical guides written by someone who really runs events: choosing the DJ, prices and music that works on the floor. (Articles in Italian)",
    "blog.readMore": "Read the guide",
    // FAQ
    "faq.eyebrow": "Frequently Asked Questions",
    "faq.title": "FAQ — Everything You Want to Know",
    "faq.accent": "Answers to the questions I receive most often. Don't find yours? Message me on WhatsApp.",
    "faq.items": [
      { q: "How much does a wedding DJ cost in Ticino?", a: "From CHF 150 upwards: private parties CHF 200-300, weddings from CHF 500 with sound and lighting rig included. The final price depends on duration, special effects and travel. Every quote is free, written and non-binding: fill in the form or message me on WhatsApp." },
      { q: "Do you also come to Zurich, Geneva or other cities?", a: "Yes: I'm based in Lugano-Paradiso but work all over Switzerland, travelling up to about 200 km, including Zurich, Geneva, Bern, Basel and Northern Italy (Como, Milan)." },
      { q: "Is sound and lighting equipment included?", a: "Yes, I always bring my professional sound and lighting rig, suitable for events up to 1000 people, with wireless microphones for speeches and ceremonies. You don't need to rent anything." },
      { q: "Can we choose the songs?", a: "Absolutely: we build the playlist together before the event, with your favourite tracks and the list of absolute 'no's. During the party I read the floor and welcome guests' requests." },
      { q: "What music genres do you play?", a: "My trademark: 80s/90s revival, disco dance, afro house and techno. But the repertoire spans from the 70s to today's hits, to make every generation dance." },
      { q: "Do you also do karaoke and entertainment?", a: "Yes: professional karaoke with thousands of backing tracks and, on request, entertainment with musical games between sets. Perfect for birthdays and village festivals." },
      { q: "How far in advance should I book?", a: "The sooner the better: weekends between May and September go fast. As soon as you have the date, contact me and we'll lock it in together." },
      { q: "Do you speak other languages for international events?", a: "I host every event in Italian. For weddings and parties with international guests we prepare the playlist and announcements together in advance, and this website is also available in English and German." },
      { q: "Do you offer special effects like cold sparks?", a: "Yes, on request: cold sparks for the first dance or the cake cutting, scenic lighting effects and laser shows for a spectacular entrance or finale." },
    ],
    // Referral
    "referral.eyebrow": "Word of Mouth",
    "referral.title": "Refer a Friend",
    "referral.accent":
      "You are the best advertising. For every friend who books, the discount is double: for them and for you.",
    "referral.steps": [
      { title: "Share", text: "Scan the QR code or send the link to your friends via WhatsApp or email." },
      { title: "Your friend mentions you", text: "In the contact form they write your name in the 'Who referred you?' field." },
      { title: "Discount for both", text: "If they book, they get 10% off their first event and you get it on your next one." },
    ],
    "referral.qrCaption": "Scan to open the site gallery",
    "referral.boxTitle": "Share DJ Magix in 10 seconds",
    "referral.boxText":
      "Show the QR code to friends during a night out, or send the ready-made message. When a friend fills in the form and enters your name, I'll reserve the discount for both of you on your next event.",
    "referral.shareText":
      "I recommend DJ Magix for your event — 25 years of experience, 80s/90s sound, disco dance and more. Have a look: {url} — if you book, write my name in the 'Who referred you?' field: discount for both of us!",
    "referral.emailSubject": "The DJ for your event — DJ Magix",
    "referral.copied": "Message copied — paste it wherever you like",
    "referral.copyFail": "Copy failed — select the link manually",
    "referral.copy": "Copy message",
    "referral.copiedBtn": "Copied",
    // Contact
    "contact.eyebrow": "Contact",
    "contact.title": "Book Your DJ Set",
    "contact.accent": "Tell me about your event: you'll receive a free, non-binding quote.",
    "contact.phone": "Phone",
    "contact.availability":
      "Available throughout Switzerland: Ticino, Lugano, Zurich, Geneva — and on request in Northern Italy (Como, Milan).",
    "contact.name": "First and Last Name *",
    "contact.phonePh": "Phone",
    "contact.eventType": "Event type",
    "contact.eventTypes": ["Birthday", "Village Festival", "Wedding", "Private Party", "Corporate Event", "Club / Venue", "Other"],
    "contact.dateAria": "Event date",
    "contact.city": "City / Venue",
    "contact.guests": "Number of guests (approx.)",
    "contact.message": "Tell me about your event...",
    "contact.referrer": "Who referred you? (optional)",
    "contact.privacyPre": "I have read and accept the",
    "contact.privacyAnd": "and the",
    "contact.terms": "Terms of Service",
    "contact.newsletter": "I want to receive news and offers by email (optional — unsubscribe with one click)",
    "contact.submit": "Send Request",
    "contact.sending": "Sending...",
    "contact.privacyError": "You must accept the Privacy Policy to send the request.",
    "contact.success": "Request sent! I'll get back to you as soon as possible.",
    "contact.error": "Something went wrong. Please try again or call me directly.",
    // Footer
    "footer.desc": "High-end sets from Lugano-Paradiso across Switzerland. 25 years of experience, over 1500 events.",
    "footer.pages": "Pages",
    "footer.contacts": "Contact",
    "footer.directories": "Find us also on",
    "footer.cookies": "Cookie preferences",
    "footer.rights": "© 2026 DJ Magix — Tutta Musica. Last updated: October 2026.",
    "footer.follow": "Follow DJ Magix on Instagram",
    "footer.call": "Call DJ Magix",
    "footer.mail": "Email DJ Magix",
    // Cookie banner
    "cookie.title": "We respect your privacy",
    "cookie.text":
      "We use technical cookies and — only with your consent — analytics tools (Google Analytics 4 with anonymised IP and Microsoft Clarity) to improve the site. No advertising, no data selling. Details in the",
    "cookie.accept": "Accept",
    "cookie.reject": "Reject",
    // WhatsApp float
    "wa.float": "Message me on WhatsApp",
    "wa.aria": "Message DJ Magix on WhatsApp",
    "wa.message": "Hi DJ Magix! I'd like information for an event.",
    // Video section
    "video.eyebrow": "Live",
    "video.title": "Watch the Party",
    "video.accent": "No advertising edits: this is a real night, filmed live.",
    "video.fallback": "Your browser does not support the video.",
    // Blog index + post UI
    "blog.pageEyebrow": "Blog",
    "blog.pageTitle": "Guides for Your Event",
    "blog.pageAccent": "Practical advice from 25 years of dancefloors: prices, music and organisation for weddings and parties in Ticino and Switzerland.",
    "blogpost.notFound": "Article not found",
    "blogpost.back": "Back to blog",
    "blogpost.by": "by",
    "blogpost.published": "published on",
    "blogpost.authorLabel": "About the author",
    "blogpost.authorBio": "DJ and producer with 25 years of experience and over 1500 events across Ticino, Switzerland and Northern Italy: weddings, village festivals, clubs and corporate events. The guides on this blog come from what I see really working on the floor.",
    "blogpost.ctaTitle": "Shall we plan your event?",
    "blogpost.ctaText": "Free, non-binding quote — personal reply guaranteed.",
    "blogpost.ctaQuote": "Free Quote",
    // Install app (PWA)
    "install.title": "DJ Magix always in your pocket",
    "install.text": "Install the free app: mixes, gallery and quotes in one tap.",
    "install.ios": "On iPhone: open the Share menu (square with arrow) and choose 'Add to Home Screen'.",
    "install.button": "Install",
    "install.dismiss": "Not now",
  },

  de: {
    // Navbar
    "nav.services": "Leistungen",
    "nav.genres": "Genres",
    "nav.experience": "Über mich",
    "nav.gallery": "Galerie",
    "nav.contact": "Kontakt",
    // Hero
    "hero.badge": "25 Jahre Erfahrung",
    "hero.srTitle": "DJ-Sets für Hochzeiten, Feste und Events in der Schweiz — ",
    "hero.tagline": "Musik • Emotion • Technologie",
    "hero.subtitle":
      "Hochwertige DJ-Sets in der ganzen Schweiz: Geburtstage, Dorffeste, Hochzeiten, Clubs und Firmenevents. Producer & massgeschneiderter Sound.",
    "hero.ctaQuote": "Gratis Offerte anfordern",
    "hero.ctaCall": "Jetzt anrufen",
    "hero.scroll": "Scrollen",
    // StatsBar
    "stats.items": [
      "25+ Jahre Karriere",
      "1500+ realisierte Events",
      "Professionelle Audio- & Lichtanlage",
      "Dorffeste & Clubs in der Schweiz",
      "Kalte Fontänen & Spezialeffekte",
      "Producer & Custom Edits",
    ],
    // Intro
    "intro.title": "Der DJ für Ihren Anlass in der Schweiz",
    "intro.p1":
      "Sie planen eine Hochzeit am Luganersee? Ein Dorffest im Tessin, einen besonderen Geburtstag oder einen Firmenanlass? Mit Magix haben Sie einen einzigen Profi für Musik, Audio und Licht. Seit 25 Jahren bringe ich die italienische Schweiz zum Tanzen — mit über 1500 Events.",
    "intro.p2":
      "Das Repertoire reicht vom 80s/90s-Revival über Disco Dance bis Afro House und Techno. Die Playlist erstellen wir gemeinsam, Wochen vor dem Termin. Und bei Bedarf besichtige ich die Location im Voraus.",
    "intro.bullets": [
      "Professionelles Audio und Licht bis 1000 Gäste",
      "Massgeschneiderte Musik-Playlist",
      "Kostenlose Besichtigung der Location",
      "Drahtlose Mikrofone für Reden und Zeremonie",
    ],
    "intro.p3":
      "Ich arbeite im ganzen Tessin und fahre bis Zürich, Genf, Bern und Basel. Ich decke auch Norditalien ab: Como, Mailand und Varese. Die Preise beginnen bei CHF 150. Die Offerte ist gratis und unverbindlich.",
    "intro.links": [
      "Hochzeits-DJ in Lugano",
      "Hochzeits-DJ im Tessin",
      "Hochzeits-DJ Comersee",
      "Firmenevents in der Schweiz",
      "Geburtstage & private Feste",
      "Dorffeste & Festivals",
    ],
    // Services
    "services.eyebrow": "Meine Leistungen",
    "services.title": "DJ-Sets auf höchstem Niveau",
    "services.accent":
      "Jeder Anlass ist eine massgeschneiderte Produktion: Musik, Licht und Emotionen bis ins kleinste Detail gepflegt.",
    "services.items": [
      { title: "Dorffeste & Chilbi", tag: "Volksenergie & grosse Plätze", desc: "Jahrzehntelange Erfahrung, ganze Gemeinden zum Tanzen zu bringen: Crowd-Management, starke Beschallung und mitreissende Playlists für alle Generationen." },
      { title: "Geburtstage & 18. Geburtstage", tag: "Exklusive & persönliche Party", desc: "Vom 18. bis zum 50. und darüber hinaus: massgeschneiderte Playlist mit Ihren Lieblingssongs, professionelles Karaoke und Architekturlicht." },
      { title: "Hochzeiten der Extraklasse", tag: "Tessin & Comersee", desc: "Ein makelloser Soundtrack von der Zeremonie über den Apéro bis zur ausgelassenen Party, mit kalten Fontänen und stimmungsvollen Lichteffekten." },
      { title: "Clubs, Diskotheken & Lounges", tag: "Beat Precision & Club Culture", desc: "Energische DJ-Sets, flüssiges Mixing und aktuelle Tracklists, mit Residenzen und Nächten in den renommiertesten Clubs der Schweiz." },
      { title: "Firmenevents & Galas", tag: "Corporate & Brand Launch", desc: "Elegante Atmosphäre für Firmenessen, Produkteinführungen und Kongresse, mit drahtlosen Mikrofonen und makelloser Audioregie." },
      { title: "DJ Producer & Custom Edits", tag: "Studio & eigene Produktionen", desc: "Exklusive Remixe, originelle Mashups und Produktionen, die Ihrem Anlass eine einzigartige musikalische Identität verleihen." },
    ],
    // Genres
    "genres.eyebrow": "Der Sound",
    "genres.title": "Die Genres des DJ-Sets",
    "genres.accent":
      "Vier musikalische Seelen, eine Regie der Tanzfläche. Tippen Sie auf ein Genre und hören Sie die Vorschau.",
    "genres.items": [
      { title: "80s/90s", sub: "Revival Dance", desc: "Die grossen Klassiker, die Dance-Geschichte geschrieben haben: von Gigi D'Agostino bis zu zeitlosen Hymnen. Madonna, Spagna, Baltimora und Eiffel 65 auf den Decks: Mitsinggarantie ab dem ersten Track." },
      { title: "Disco Dance", sub: "Nu-Disco & Funk", desc: "Zeitlose Grooves, warme Basslines und Glitzer: Die Tanzfläche leuchtet mit dem elegantesten Disco-Sound. Von Earth, Wind & Fire bis Daft Punk — der richtige Groove für jede Generation." },
      { title: "Afro House", sub: "Deep & Tribal", desc: "Hypnotische Percussion und tiefe Atmosphären: der Sound, der die Clubs weltweit erobert. Eine Reise zwischen afrikanischen Wurzeln und moderner Elektronik, perfekt bei Sonnenuntergang." },
      { title: "Techno", sub: "Melodic & Driving", desc: "Kraftvolle Beats und einhüllende Melodien für intensive Nächte bis in den späten Abend. Von Melodic Techno der europäischen Festivals bis zu härteren Klängen, immer massgeschneidert für die Tanzfläche." },
    ],
    "genres.listen": "Mix anhören",
    "genres.playing": "Läuft — tippen zum Stoppen",
    // MixPlayer
    "mix.label": "Neuester Mix — Gratis Download",
    "mix.play": "Abspielen",
    "mix.pause": "Pause",
    "mix.download": "Mix herunterladen",
    // About
    "about.eyebrow": "Über mich",
    "about.title": "25 Jahre Leidenschaft hinter dem Pult",
    "about.quote": "Ein Vierteljahrhundert Musik, Emotionen und Technologie.",
    "about.p1":
      "Seit über 25 Jahren bringt DJ Magix die Tanzflächen der Schweiz und Norditaliens zum Beben: von Tessiner Dorffesten bis zu exklusiven Clubs, von Traumhochzeiten bis zu grossen Firmenevents. Seine Stärke ist es, die Tanzfläche zu lesen und die perfekte Alchemie zwischen Generationen und Genres zu schaffen.",
    "about.p2":
      "Als Producer erstellt er exklusive Remixe und Custom Edits: Jeder Anlass hat einen Soundtrack, den Sie nirgendwo sonst finden.",
    "about.p3":
      "Animator und Tänzer sowie Producer: Wenn nötig, geht er auf die Tanzfläche und reisst auch die schüchternsten Gäste mit. Er arbeitet in Villen, Schlössern, Restaurants und auf Plätzen. Jeder Anlass wird auf Italienisch moderiert, mit im Voraus vorbereiteter Playlist und Ansagen — nützlich, wenn Gäste aus aller Welt anreisen.",
    "about.highlights": ["Jahre Erfahrung", "Realisierte Events", "Leidenschaft & Hingabe"],
    // Gallery
    "gallery.eyebrow": "Galerie",
    "gallery.title": "Goldene Atmosphären & Momente",
    "gallery.accent": "Einblicke in die Nächte, die die Schweiz zum Tanzen brachten.",
    "gallery.items": [
      { alt: "Hochzeit mit Konfetti und 360-Fotobox", caption: "Wedding — 360° Party" },
      { alt: "Fest in einem Tessiner Lokal mit Disco-Lichtern von DJ Magix", caption: "Live — Lokal-Lichter" },
      { alt: "DJ-Pult im Nachtclub", caption: "Decks — Club Night" },
      { alt: "Publikum am abendlichen Dorffest am Seeufer mit DJ Magix", caption: "Dorffest — Am Abend" },
      { alt: "Goldene Funken", caption: "Funken" },
      { alt: "Brautpaar zwischen den Funken", caption: "Wedding — First Dance" },
    ],
    // Testimonials
    "testimonials.eyebrow": "Bewertungen",
    "testimonials.title": "Was man über mich sagt",
    "testimonials.accent": "Die Zufriedenheit derer, die mit DJ Magix getanzt haben.",
    "testimonials.badge.pre": "auf Matrimonio.com · Empfohlen von",
    "testimonials.badge.post": "der Paare",
    // Case Studies
    "cases.eyebrow": "Case Study",
    "cases.title": "Ausgewählte Events",
    "cases.accent": "Zwei Zusammenarbeiten, auf die ich besonders stolz bin.",
    "cases.items": [
      { tag: "Öffentliche Institution — Dorffest", title: "Gemeinde Paradiso (Lugano)", text: "Für das öffentliche Fest der Gemeinde Paradiso habe ich den ganzen Abend betreut: Audio- und Lichtanlage für den Platz, eine Playlist für alle Generationen und ein grosses Finale mit voller Tanzfläche. Eine Ehre, für meine Heimatgemeinde zu spielen." },
      { tag: "Eventagentur — International", title: "Kika Events (Brasilien)", text: "Die brasilianische Agentur Kika Events hat mich für den musikalischen Teil eines internationalen Privatanlasses in der Schweiz gewählt: mehrsprachiges DJ-Set, Fernkoordination mit der Agentur und ein Abend, der Gäste von zwei Kontinenten vereinte." },
    ],
    // Blog teaser
    "blog.eyebrow": "Aus dem Blog",
    "blog.title": "Tipps für Ihren Anlass",
    "blog.accent":
      "Praktische Ratgeber von jemandem, der Events wirklich macht: DJ-Wahl, Preise und Musik, die auf der Tanzfläche funktioniert. (Artikel auf Italienisch)",
    "blog.readMore": "Ratgeber lesen",
    // FAQ
    "faq.eyebrow": "Häufige Fragen",
    "faq.title": "FAQ — Alles, was Sie wissen möchten",
    "faq.accent": "Antworten auf die häufigsten Fragen. Ihre ist nicht dabei? Schreiben Sie mir auf WhatsApp.",
    "faq.items": [
      { q: "Was kostet ein Hochzeits-DJ im Tessin?", a: "Ab CHF 150 aufwärts: private Feste CHF 200-300, Hochzeiten ab CHF 500 inklusive Audio- und Lichtanlage. Der Endpreis hängt von Dauer, Spezialeffekten und Anfahrt ab. Jede Offerte ist gratis, schriftlich und unverbindlich: Formular ausfüllen oder auf WhatsApp schreiben." },
      { q: "Kommen Sie auch nach Zürich, Genf oder in andere Städte?", a: "Ja: Ich bin in Lugano-Paradiso zu Hause, arbeite aber in der ganzen Schweiz mit Anfahrten bis ca. 200 km, inklusive Zürich, Genf, Bern, Basel und Norditalien (Como, Mailand)." },
      { q: "Ist die Audio- und Lichtanlage inbegriffen?", a: "Ja, ich bringe immer meine professionelle Audio- und Lichtanlage mit, geeignet für Anlässe bis 1000 Personen, mit drahtlosen Mikrofonen für Reden und Zeremonien. Sie müssen nichts mieten." },
      { q: "Können wir die Songs auswählen?", a: "Unbedingt: Die Playlist erstellen wir gemeinsam vor dem Anlass, mit Ihren Lieblingssongs und der Liste der absoluten 'No-Gos'. Während der Party lese ich die Tanzfläche und nehme Musikwünsche der Gäste entgegen." },
      { q: "Welche Musikgenres spielen Sie?", a: "Mein Markenzeichen: 80s/90s-Revival, Disco Dance, Afro House und Techno. Das Repertoire reicht aber von den 70ern bis zu den Hits von heute, um alle Generationen zum Tanzen zu bringen." },
      { q: "Bieten Sie auch Karaoke und Animation?", a: "Ja: professionelles Karaoke mit Tausenden von Playbacks und auf Wunsch Animation mit Musikspielen zwischen den Sets. Perfekt für Geburtstage und Dorffeste." },
      { q: "Wie früh muss ich buchen?", a: "Je früher, desto besser: Die Wochenenden zwischen Mai und September sind schnell vergeben. Sobald Sie das Datum haben, kontaktieren Sie mich und wir reservieren es gemeinsam." },
      { q: "Sprechen Sie andere Sprachen für internationale Anlässe?", a: "Ich moderiere jeden Anlass auf Italienisch. Für Hochzeiten und Feste mit internationalen Gästen bereiten wir Playlist und Ansagen gemeinsam im Voraus vor — und diese Website ist auch auf Englisch und Deutsch verfügbar." },
      { q: "Bieten Sie Spezialeffekte wie kalte Fontänen?", a: "Ja, auf Wunsch: kalte Fontänen für den ersten Tanz oder den Kuchenanschnitt, stimmungsvolle Lichteffekte und Lasershows für einen spektakulären Einzug oder Abschluss." },
    ],
    // Referral
    "referral.eyebrow": "Mundpropaganda",
    "referral.title": "Freunde werben",
    "referral.accent":
      "Die beste Werbung sind Sie. Für jeden Freund, der bucht, gibt es doppelten Rabatt: für ihn und für Sie.",
    "referral.steps": [
      { title: "Teilen", text: "Scannen Sie den QR-Code oder senden Sie den Link per WhatsApp oder E-Mail an Ihre Freunde." },
      { title: "Ihr Freund erwähnt Sie", text: "Im Kontaktformular schreibt er Ihren Namen ins Feld 'Wer hat Sie empfohlen?'." },
      { title: "Rabatt für beide", text: "Wenn er bucht, erhält er 10% Rabatt auf den ersten Anlass und Sie auf Ihren nächsten." },
    ],
    "referral.qrCaption": "Scannen und Galerie öffnen",
    "referral.boxTitle": "DJ Magix in 10 Sekunden teilen",
    "referral.boxText":
      "Zeigen Sie den QR-Code Freunden während eines Anlasses oder senden Sie die fertige Nachricht. Wenn ein Freund das Formular ausfüllt und Ihren Namen einträgt, reserviere ich den Rabatt für beide beim nächsten Anlass.",
    "referral.shareText":
      "Ich empfehle DJ Magix für Ihren Anlass — 25 Jahre Erfahrung, 80s/90s-Sound, Disco Dance und mehr. Schauen Sie hier: {url} — bei einer Buchung schreiben Sie meinen Namen ins Feld 'Wer hat Sie empfohlen?': Rabatt für uns beide!",
    "referral.emailSubject": "Der DJ für Ihren Anlass — DJ Magix",
    "referral.copied": "Nachricht kopiert — überall einfügen",
    "referral.copyFail": "Kopieren fehlgeschlagen — Link manuell auswählen",
    "referral.copy": "Nachricht kopieren",
    "referral.copiedBtn": "Kopiert",
    // Contact
    "contact.eyebrow": "Kontakt",
    "contact.title": "Buchen Sie Ihr DJ-Set",
    "contact.accent": "Erzählen Sie mir von Ihrem Anlass: Sie erhalten eine gratis, unverbindliche Offerte.",
    "contact.phone": "Telefon",
    "contact.availability":
      "In der ganzen Schweiz verfügbar: Tessin, Lugano, Zürich, Genf — und auf Wunsch in Norditalien (Como, Mailand).",
    "contact.name": "Vor- und Nachname *",
    "contact.phonePh": "Telefon",
    "contact.eventType": "Art des Anlasses",
    "contact.eventTypes": ["Geburtstag", "Dorffest", "Hochzeit", "Privates Fest", "Firmenevent", "Club / Lokal", "Anderes"],
    "contact.dateAria": "Datum des Anlasses",
    "contact.city": "Stadt / Location",
    "contact.guests": "Anzahl Gäste (ca.)",
    "contact.message": "Erzählen Sie mir von Ihrem Anlass...",
    "contact.referrer": "Wer hat Sie empfohlen? (optional)",
    "contact.privacyPre": "Ich habe die",
    "contact.privacyAnd": "gelesen und akzeptiere sie sowie die",
    "contact.terms": "AGB",
    "contact.newsletter": "Ich möchte Neuigkeiten und Angebote per E-Mail erhalten (optional — Abmeldung mit einem Klick)",
    "contact.submit": "Anfrage senden",
    "contact.sending": "Wird gesendet...",
    "contact.privacyError": "Sie müssen die Datenschutzerklärung akzeptieren, um die Anfrage zu senden.",
    "contact.success": "Anfrage gesendet! Ich melde mich so schnell wie möglich.",
    "contact.error": "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut oder rufen Sie direkt an.",
    // Footer
    "footer.desc": "Hochwertige Sets von Lugano-Paradiso in die ganze Schweiz. 25 Jahre Erfahrung, über 1500 Events.",
    "footer.pages": "Seiten",
    "footer.contacts": "Kontakt",
    "footer.directories": "Sie finden uns auch auf",
    "footer.cookies": "Cookie-Einstellungen",
    "footer.rights": "© 2026 DJ Magix — Tutta Musica. Letzte Aktualisierung: Oktober 2026.",
    "footer.follow": "DJ Magix auf Instagram folgen",
    "footer.call": "DJ Magix anrufen",
    "footer.mail": "E-Mail an DJ Magix",
    // Cookie banner
    "cookie.title": "Wir respektieren Ihre Privatsphäre",
    "cookie.text":
      "Wir verwenden technische Cookies und — nur mit Ihrer Einwilligung — Analysewerkzeuge (Google Analytics 4 mit anonymisierter IP und Microsoft Clarity), um die Website zu verbessern. Keine Werbung, kein Datenverkauf. Details in der",
    "cookie.accept": "Akzeptieren",
    "cookie.reject": "Ablehnen",
    // WhatsApp float
    "wa.float": "Schreiben Sie mir auf WhatsApp",
    "wa.aria": "DJ Magix auf WhatsApp schreiben",
    "wa.message": "Hallo DJ Magix! Ich möchte Informationen für einen Anlass.",
    // Video section
    "video.eyebrow": "Live",
    "video.title": "Die Party ansehen",
    "video.accent": "Keine Werbeschnitte: Das ist ein echter Abend, live gefilmt.",
    "video.fallback": "Ihr Browser unterstützt das Video nicht.",
    // Install app (PWA)
    "install.title": "DJ Magix immer in der Tasche",
    "install.text": "Installieren Sie die Gratis-App: Mixe, Galerie und Offerten mit einem Fingertipp.",
    "install.ios": "Auf dem iPhone: Teilen-Symbol (Quadrat mit Pfeil) öffnen und 'Zum Home-Bildschirm' wählen.",
    "install.button": "Installieren",
    "install.dismiss": "Nicht jetzt",
  },
};
